var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
import { useState, useRef, useCallback, useEffect } from 'react';
import { base64ToArrayBuffer, convertPCM16ToFloat32 } from '../utils/audio';
/**
 * Hook for real-time audio playback, with support for interruptions via localStorage.
 *
 * @param analytics - An analytics instance for error tracking.
 * @returns An API for managing live audio playback with queue controls.
 */
export function useRealTimeAudioPlayer(analytics) {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioContextRef = useRef(null);
  const audioQueueRef = useRef([]);
  const currentSourceRef = useRef(null);
  const isProcessingQueueRef = useRef(false);
  // Ensure AudioContext initialization
  const ensureAudioContext = useCallback(() => __awaiter(this, void 0, void 0, function* () {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }
    if (audioContextRef.current.state === 'suspended') {
      yield audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }), []);
  // localStorage being the SOT, checking for interruption
  const isInterrupted = useCallback(() => {
    return localStorage.getItem('isAudioInterrupted') === 'yes';
  }, []);
  // interruption Handler
  const handleInterruption = useCallback(() => __awaiter(this, void 0, void 0, function* () {
    if (isInterrupted()) {
      // Stop current playback
      if (currentSourceRef.current) {
        currentSourceRef.current.stop();
        currentSourceRef.current.disconnect();
        currentSourceRef.current = null;
      }
      // Clear the queue and reset states
      audioQueueRef.current = [];
      isProcessingQueueRef.current = false;
      setIsPlaying(false);
    }
  }), [isInterrupted]);
  // audio chunk processor
  const processAudioChunk = useCallback(({
    base64AudioData,
    sampleRate
  }) => __awaiter(this, void 0, void 0, function* () {
    const audioContext = yield ensureAudioContext();
    const arrayBuffer = base64ToArrayBuffer(base64AudioData);
    const pcm16Array = new Int16Array(arrayBuffer);
    const float32Array = convertPCM16ToFloat32(pcm16Array);
    if (!float32Array || float32Array.some(isNaN)) {
      throw new Error('Invalid audio data.');
    }
    const buffer = audioContext.createBuffer(1, float32Array.length, sampleRate);
    buffer.copyToChannel(float32Array, 0);
    return buffer;
  }), [ensureAudioContext]);
  // function to handle playback of the next audio chunk in the queue
  const playNextInQueue = useCallback(() => __awaiter(this, void 0, void 0, function* () {
    if (isProcessingQueueRef.current || audioQueueRef.current.length === 0) {
      return;
    }
    isProcessingQueueRef.current = true;
    try {
      // best to check for interruption before processing the next item in the queue
      if (isInterrupted()) {
        yield handleInterruption();
        return;
      }
      const nextItem = audioQueueRef.current.shift();
      if (!nextItem) {
        isProcessingQueueRef.current = false;
        setIsPlaying(false);
        return;
      }
      const buffer = yield processAudioChunk(nextItem);
      const audioContext = yield ensureAudioContext();
      // This would ensure that we do not have race conditions that would cause the audio to play twice
      if (currentSourceRef.current) {
        currentSourceRef.current.stop();
        currentSourceRef.current.disconnect();
      }
      // connecting to a source node to play the audio
      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext.destination);
      currentSourceRef.current = source;
      // cleaning up resources onend of the source
      source.onended = () => {
        currentSourceRef.current = null;
        isProcessingQueueRef.current = false;
        // Check interrupt flag before proceeding
        if (audioQueueRef.current.length > 0 && !isInterrupted()) {
          playNextInQueue().catch(error => analytics.logError({
            loggedFrom: 'useRealTimeAudioPlayer.playNextInQueue',
            message: 'Error playing next audio chunk.',
            error
          }));
        } else {
          setIsPlaying(false);
        }
      };
      // Start playback
      source.start();
      setIsPlaying(true);
    } catch (error) {
      analytics.logError({
        loggedFrom: 'useRealTimeAudioPlayer.playNextInQueue',
        message: 'Failed to play audio chunk.',
        error
      });
      // eslint-disable-next-line require-atomic-updates
      isProcessingQueueRef.current = false;
      // Retry playing if there are items in the queue and no interruption
      if (audioQueueRef.current.length > 0 && !isInterrupted()) {
        playNextInQueue().catch(console.error);
      }
    }
  }), [processAudioChunk, ensureAudioContext, handleInterruption, isInterrupted, analytics]);
  // Add audio to the queue and start playback
  const playAudio = useCallback((base64AudioData, sampleRate = 24000) => __awaiter(this, void 0, void 0, function* () {
    if (!base64AudioData) return;
    audioQueueRef.current.push({
      base64AudioData,
      sampleRate
    });
    if (!isPlaying && !isProcessingQueueRef.current && !isInterrupted()) {
      yield playNextInQueue();
    }
  }), [isPlaying, playNextInQueue, isInterrupted]);
  // Function to stop Playback and clear the queue.
  const stopPlayback = useCallback(() => __awaiter(this, void 0, void 0, function* () {
    if (currentSourceRef.current) {
      currentSourceRef.current.stop();
      currentSourceRef.current.disconnect();
      currentSourceRef.current = null;
    }
    audioQueueRef.current = [];
    isProcessingQueueRef.current = false;
    setIsPlaying(false);
  }), []);
  // Need a reactor/listener to listen for storage changes
  useEffect(() => {
    const onStorageChange = event => {
      if (event.key === 'isAudioInterrupted') {
        handleInterruption().catch(console.error);
      }
    };
    window.addEventListener('storage', onStorageChange);
    const interval = setInterval(() => handleInterruption().catch(console.error), 300);
    return () => {
      var _a;
      window.removeEventListener('storage', onStorageChange);
      clearInterval(interval);
      if (audioContextRef.current) {
        (_a = audioContextRef.current.close()) === null || _a === void 0 ? void 0 : _a.catch(console.error);
        audioContextRef.current = null;
      }
    };
  }, [handleInterruption]);
  return {
    isPlaying,
    queueLength: audioQueueRef.current.length,
    playAudio,
    stopPlayback,
    clearQueue: stopPlayback
  };
}